// src/utils/versionCheck.ts
// A tab left open across a deploy keeps running the old build (index.html is
// never cached, but a single page app never reloads it). Every few minutes and
// whenever the tab comes back into view, fetch index.html and compare its main
// script with the one this page loaded. When a new build is out, reload at the
// next route change, so nobody loses an answer or a dialog mid-action.

const CHECK_EVERY_MS = 5 * 60 * 1000;
let staleBuild = false;

function scriptOf(html: string): string | null {
  const m = /\/static\/js\/main\.[a-z0-9]+\.js/.exec(html);
  return m ? m[0] : null;
}

function currentScript(): string | null {
  const el = Array.from(document.scripts).find((s) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(s.src));
  return el ? scriptOf(el.src) : null;
}

async function check(): Promise<void> {
  if (staleBuild) return;
  const mine = currentScript();
  if (!mine) return; // dev server: no hashed bundle
  try {
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return;
    const live = scriptOf(await res.text());
    if (live && live !== mine) staleBuild = true;
  } catch {
    // offline: try again later
  }
}

/** Start watching for new deploys (production builds only). */
export function installVersionCheck(): void {
  if (process.env.NODE_ENV !== "production" || typeof window === "undefined") return;
  window.setInterval(() => void check(), CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
}

/** Call on every route change: reloads once a newer build is live. */
export function reloadIfStale(): void {
  if (staleBuild) window.location.reload();
}
